import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, apiDownload, apiRequest, apiUpload } from "../../api/client";
import type { Aliases, DictionaryKind, DictionaryStats, DictionarySummary, ImportSummary, TerminologyPriority, TerminologyTerm } from "../../api/types";
import { useAuth } from "../../context/AuthContext";
import { KindBadge } from "./DictionariesPage";

// "Talent; HR" -> ["Talent", "HR"]  (";" or new lines separate aliases; commas are allowed inside a term)
const parseList = (s: string) => [...new Set(s.split(/[;\n؛]/).map((x) => x.trim()).filter(Boolean))];
const showList = (l?: string[]) => (l ?? []).join("; ");

const emptyForm = {
  englishTerm: "", arabicTerm: "", frenchTerm: "", category: "General", priority: "NORMAL" as TerminologyPriority,
  pronunciation: "", notes: "", enAliases: "", arAliases: "", frAliases: "",
};

export default function DictionaryPage() {
  const { dictionaryId = "" } = useParams<{ dictionaryId: string }>();
  const { admin } = useAuth();
  const isSuper = admin?.role === "SUPER_ADMIN";

  const [dictionary, setDictionary] = useState<DictionarySummary | null>(null);
  const [terms, setTerms] = useState<TerminologyTerm[] | null>(null);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TerminologyTerm | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<DictionaryStats | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ name: "", description: "", kind: "INDUSTRY" as DictionaryKind, isActive: true });
  const fileInput = useRef<HTMLInputElement | null>(null);
  const latestLoad = useRef(0);

  // Changing AFA's central dictionary needs a super administrator (the server enforces this too).
  const locked = dictionary?.kind === "CENTRAL" && !isSuper;

  async function loadDictionary() {
    const all = await apiRequest<DictionarySummary[]>("/dictionaries");
    setDictionary(all.find((d) => d.id === dictionaryId) ?? null);
  }
  async function loadTerms(q?: string) {
    const ticket = ++latestLoad.current; // only the newest search may update the table
    const rows = await apiRequest<TerminologyTerm[]>(`/dictionaries/${dictionaryId}/terms`, { query: { q } });
    if (ticket === latestLoad.current) setTerms(rows);
  }
  useEffect(() => {
    void loadDictionary();
    void loadTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictionaryId]);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const timer = setTimeout(() => void loadTerms(query.trim() || undefined), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (showStats) void apiRequest<DictionaryStats>(`/dictionaries/${dictionaryId}/stats`).then(setStats).catch(() => setStats(null));
  }, [showStats, dictionaryId]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
    setShowForm(true);
  }
  function openEdit(t: TerminologyTerm) {
    setEditing(t);
    setForm({
      englishTerm: t.englishTerm, arabicTerm: t.arabicTerm, frenchTerm: t.frenchTerm, category: t.category ?? "General",
      priority: t.priority, pronunciation: t.pronunciation ?? "", notes: t.notes ?? "",
      enAliases: showList(t.aliases.en), arAliases: showList(t.aliases.ar), frAliases: showList(t.aliases.fr),
    });
    setError(null);
    setShowForm(true);
  }

  async function saveTerm() {
    setBusy(true);
    setError(null);
    const aliases: Aliases = { en: parseList(form.enAliases), ar: parseList(form.arAliases), fr: parseList(form.frAliases) };
    const body = {
      englishTerm: form.englishTerm, arabicTerm: form.arabicTerm, frenchTerm: form.frenchTerm, category: form.category || "General",
      priority: form.priority, pronunciation: form.pronunciation || null, notes: form.notes || null, aliases,
    };
    try {
      if (editing) await apiRequest(`/dictionaries/${dictionaryId}/terms/${editing.id}`, { method: "PUT", body });
      else await apiRequest(`/dictionaries/${dictionaryId}/terms`, { method: "POST", body });
      setShowForm(false);
      setNotice("Saved. Running sessions use the change from their next sentence.");
      await Promise.all([loadTerms(query.trim() || undefined), loadDictionary()]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the term");
    } finally {
      setBusy(false);
    }
  }

  async function removeTerm(t: TerminologyTerm) {
    if (!confirm(`Delete "${t.englishTerm}" from this dictionary?`)) return;
    try {
      await apiRequest(`/dictionaries/${dictionaryId}/terms/${t.id}`, { method: "DELETE" });
      await Promise.all([loadTerms(query.trim() || undefined), loadDictionary()]);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : "Could not delete the term");
    }
  }

  async function onImportFile(file: File) {
    setImportResult(null);
    setNotice(null);
    try {
      setImportResult(await apiUpload<ImportSummary>(`/dictionaries/${dictionaryId}/import`, file));
      await Promise.all([loadTerms(query.trim() || undefined), loadDictionary()]);
    } catch (err) {
      setNotice(err instanceof ApiError ? `Import failed: ${err.message}` : "Import failed");
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  function openSettings() {
    if (!dictionary) return;
    setSettings({ name: dictionary.name, description: dictionary.description ?? "", kind: dictionary.kind, isActive: dictionary.isActive });
    setError(null);
    setShowSettings(true);
  }
  async function saveSettings() {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/dictionaries/${dictionaryId}`, { method: "PUT", body: { ...settings, description: settings.description || null } });
      setShowSettings(false);
      await loadDictionary();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  if (dictionary === null && terms === null) return <p>Loading…</p>;
  if (dictionary === null) return <p>Dictionary not found. <Link to="/operator/dictionaries">Back to dictionaries</Link></p>;

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Link className="btn-ghost" to="/operator/dictionaries">
          ← All dictionaries
        </Link>
      </div>
      <div className="toolbar">
        <h1 style={{ margin: 0 }}>{dictionary.name}</h1>
        <KindBadge kind={dictionary.kind} />
        {!dictionary.isActive && <span className="chip">switched off</span>}
        <div style={{ flex: 1 }} />
        <button className="btn-ghost" onClick={openSettings} disabled={locked}>
          Edit dictionary
        </button>
      </div>
      {dictionary.description && <p style={{ color: "var(--text-muted)", marginTop: 4 }}>{dictionary.description}</p>}
      {dictionary.kind === "CENTRAL" && (
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          This is AFA's central dictionary: it is loaded for every conference and always wins over every other dictionary.
          {locked && " Only a super administrator can change it."}
        </p>
      )}

      <div className="toolbar">
        <input
          placeholder="Search terms, translations, synonyms…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border)", minWidth: 260 }}
        />
        <div style={{ flex: 1 }} />
        <button className="btn-secondary" onClick={() => setShowStats((v) => !v)}>
          {showStats ? "Hide usage" : "Usage statistics"}
        </button>
        <button className="btn-secondary" onClick={() => void apiDownload(`/dictionaries/${dictionaryId}/export.xlsx`, `${dictionary.name}.xlsx`)}>
          Export Excel
        </button>
        <button className="btn-secondary" onClick={() => void apiDownload(`/dictionaries/${dictionaryId}/export.csv`, `${dictionary.name}.csv`)}>
          Export CSV
        </button>
        <button className="btn-secondary" onClick={() => fileInput.current?.click()} disabled={locked}>
          Import Excel / CSV
        </button>
        <input ref={fileInput} type="file" accept=".csv,.xlsx,text/csv" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void onImportFile(f); }} />
        <button className="btn-primary" onClick={openCreate} disabled={locked}>
          + New term
        </button>
      </div>

      {notice && <div className="card" style={{ marginBottom: 12, fontSize: 14 }}>{notice}</div>}
      {importResult && (
        <div className="card" style={{ marginBottom: 12, fontSize: 14 }}>
          <strong>Import finished:</strong> {importResult.inserted} added, {importResult.updated} updated, {importResult.skipped} skipped.
          {importResult.errors.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingInlineStart: 20 }}>
              {importResult.errors.slice(0, 10).map((e) => (
                <li key={e.row}>Row {e.row}: {e.message}</li>
              ))}
              {importResult.errors.length > 10 && <li>…and {importResult.errors.length - 10} more</li>}
            </ul>
          )}
        </div>
      )}

      {showStats && (
        <div className="card" style={{ marginBottom: 14 }}>
          {!stats ? (
            "Loading…"
          ) : (
            <div className="stack">
              <div>
                <strong>{stats.totalHits}</strong> times an approved term from this dictionary was used ·{" "}
                <strong>{stats.termsUsed}</strong> of {stats.termCount} terms used · {stats.termsNeverUsed} never used
              </div>
              {stats.topTerms.length > 0 && (
                <table className="table">
                  <thead><tr><th>Most used terms</th><th>Arabic</th><th>Times</th></tr></thead>
                  <tbody>
                    {stats.topTerms.map((t) => (
                      <tr key={t.id}><td>{t.en}</td><td dir="rtl">{t.ar}</td><td>{t.hits}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
              {stats.byConference.length > 0 && (
                <div style={{ fontSize: 13 }}>
                  By conference: {stats.byConference.map((c) => `${c.name} (${c.hits})`).join(" · ")}
                </div>
              )}
              {stats.totalHits === 0 && <span style={{ color: "var(--text-muted)" }}>Nothing yet — counts appear after a session that uses this dictionary.</span>}
            </div>
          )}
        </div>
      )}

      {terms === null && <p>Loading…</p>}
      {terms && terms.length === 0 && <p>{query ? "No terms match." : "This dictionary has no terms yet."}</p>}
      {terms && terms.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>English</th>
              <th>Arabic</th>
              <th>French</th>
              <th>Category</th>
              <th>Priority</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {terms.map((t) => {
              const aliasCount = (t.aliases.en?.length ?? 0) + (t.aliases.ar?.length ?? 0) + (t.aliases.fr?.length ?? 0);
              return (
                <tr key={t.id}>
                  <td>
                    {t.englishTerm}
                    {aliasCount > 0 && (
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        also: {[...(t.aliases.en ?? []), ...(t.aliases.ar ?? []), ...(t.aliases.fr ?? [])].join(" · ")}
                      </div>
                    )}
                  </td>
                  <td dir="rtl">{t.arabicTerm}</td>
                  <td>{t.frenchTerm}</td>
                  <td>{t.category}</td>
                  <td className={`priority-${t.priority}`}>{t.priority}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn-ghost" onClick={() => openEdit(t)} disabled={locked}>Edit</button>{" "}
                    <button className="btn-ghost" onClick={() => void removeTerm(t)} disabled={locked}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="modal-backdrop" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? "Edit term" : "New term"}</h2>
            <div className="stack">
              <div className="field">
                <label>English term</label>
                <input value={form.englishTerm} onChange={(e) => setForm({ ...form, englishTerm: e.target.value })} autoFocus />
                <small style={{ color: "var(--text-muted)" }}>Text in (brackets) is optional when spoken; use " / " between alternatives.</small>
              </div>
              <div className="field">
                <label>Preferred Arabic translation</label>
                <input dir="rtl" value={form.arabicTerm} onChange={(e) => setForm({ ...form, arabicTerm: e.target.value })} />
              </div>
              <div className="field">
                <label>Preferred French translation</label>
                <input value={form.frenchTerm} onChange={(e) => setForm({ ...form, frenchTerm: e.target.value })} />
              </div>
              <div className="field">
                <label>Synonyms, abbreviations &amp; acronyms (separate with ";")</label>
                <div className="stack" style={{ gap: 6 }}>
                  <input placeholder="English — e.g. HR; personnel department" value={form.enAliases} onChange={(e) => setForm({ ...form, enAliases: e.target.value })} />
                  <input dir="rtl" placeholder="Arabic" value={form.arAliases} onChange={(e) => setForm({ ...form, arAliases: e.target.value })} />
                  <input placeholder="French" value={form.frAliases} onChange={(e) => setForm({ ...form, frAliases: e.target.value })} />
                </div>
                <small style={{ color: "var(--text-muted)" }}>Each one, when spoken, is translated with the preferred translations above.</small>
              </div>
              <div className="grid-2">
                <div className="field">
                  <label>Category</label>
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                </div>
                <div className="field">
                  <label>Priority within this dictionary</label>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as TerminologyPriority })}>
                    <option value="LOW">Low</option>
                    <option value="NORMAL">Normal</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
              </div>
              <div className="field">
                <label>Pronunciation (optional)</label>
                <input value={form.pronunciation} onChange={(e) => setForm({ ...form, pronunciation: e.target.value })} />
              </div>
              <div className="field">
                <label>Notes (optional)</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              {error && <div className="error-banner">{error}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" disabled={busy || !form.englishTerm.trim() || !(form.arabicTerm.trim() || form.frenchTerm.trim())} onClick={saveTerm}>
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="modal-backdrop" onClick={() => setShowSettings(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit dictionary</h2>
            <div className="stack">
              <div className="field">
                <label>Name</label>
                <input value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} autoFocus />
              </div>
              <div className="field">
                <label>Description</label>
                <input value={settings.description} onChange={(e) => setSettings({ ...settings, description: e.target.value })} />
              </div>
              <div className="field">
                <label>Type</label>
                <select value={settings.kind} disabled={!isSuper} onChange={(e) => setSettings({ ...settings, kind: e.target.value as DictionaryKind })}>
                  <option value="INDUSTRY">Industry</option>
                  <option value="CONFERENCE">Conference</option>
                  <option value="CENTRAL">AFA central (loaded for every conference, highest priority)</option>
                </select>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={settings.isActive} onChange={(e) => setSettings({ ...settings, isActive: e.target.checked })} style={{ width: 18, height: 18 }} />
                Active (switch off to stop using it without deleting it)
              </label>
              {error && <div className="error-banner">{error}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowSettings(false)}>Cancel</button>
              <button className="btn-primary" disabled={busy || settings.name.trim().length < 2} onClick={saveSettings}>
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
